import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link, useParams, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  ExportProduct,
  ScreeningResult,
  ExportScreeningType,
  RunScreeningInput,
  ExportActivity,
  ProductEntityLink,
  ExportClassificationResult,
  ExportLicense,
  ScreeningAiEvaluation,
  ProductDocument,
  ProductComment,
} from 'api/ExportHubAPI';
import { JurisdictionPill, StatusPill, TierBadge } from './productPresets';
import { effectiveDestinationCountries } from './entityPresets';
import { licenseCoveredCountries } from './coverage';
import RiskScreening from './RiskScreening';
import LinkedLicenses from './LinkedLicenses';
import LicenseRequestWizard from './LicenseRequestWizard';
import SendPackageDialog from './SendPackageDialog';
import ProductStructurePanel from './ProductStructurePanel';
import JurisdictionDetailsPanel from './JurisdictionDetailsPanel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LicenseSummary from './LicenseSummary';
import DeleteProductModal from './DeleteProductModal';
import ExportWorkflowResults from './ExportWorkflowResults';
import { deriveLicenseRequirement } from './licenseRequirement';
import CustomersConsignees from './CustomersConsignees';
import EditableProductDetails from './EditableProductDetails';
import {
  PageSection,
  QuickNav,
  SectionRefreshBtn,
  HeaderBtn,
} from './productDetailShell';
import {
  ScoreCard,
  SystemFlagsCard,
  RiskCard,
  ScoreDrawer,
  RiskDrawer,
  buildScoreDeductions,
  computeScore,
  scoreBand,
  buildSystemFlags,
} from './ProductKpis';
import ProductActivity360, { ActivitySection } from './ProductActivity360';
import ProductTransactions from './ProductTransactions';

type DetailView =
  | 'overview'
  | 'transactions'
  | 'documents'
  | 'comments'
  | 'history';
const VIEWS: DetailView[] = [
  'overview',
  'transactions',
  'documents',
  'comments',
  'history',
];

// Human-readable file size for the Documents table.
const formatBytes = (n: number | null): string => {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Display type from the extension, falling back to the content type.
const docType = (d: ProductDocument): string => {
  const ext = d.file_name.includes('.') ? d.file_name.split('.').pop() : '';
  return ext ? ext.toUpperCase() : d.content_type || '—';
};

// Documents tab — real, S3-backed attachments for this product (mirrors the
// license document UI in LicensesPanel). Uploads and deletes each record a
// product activity row server-side, so `onChanged` lets the parent refetch
// History.
const DocumentsView: React.FC<{
  productId: string;
  groupId: string | undefined;
  onChanged?: () => void;
}> = ({ productId, groupId, onChanged }) => {
  const [docs, setDocs] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!groupId) return undefined;
    let active = true;
    setLoading(true);
    ExportHubAPI.getProductDocuments(productId, groupId)
      .then((d) => {
        if (active) setDocs(d);
      })
      .catch(() => {
        if (active)
          enqueueSnackbar('Failed to load documents.', { variant: 'error' });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [productId, groupId]);

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || !groupId) return;
    setUploading(true);
    try {
      const next = await ExportHubAPI.uploadProductDocument(
        productId,
        groupId,
        file,
      );
      setDocs(next);
      onChanged?.();
      enqueueSnackbar('Document uploaded.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to upload document.', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const downloadDoc = async (d: ProductDocument) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.downloadProductDocument(d.id, groupId, d.file_name);
    } catch (err) {
      enqueueSnackbar('Failed to download document.', { variant: 'error' });
    }
  };

  const deleteDoc = async (id: string) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.deleteProductDocument(id, groupId);
      setDocs((ds) => ds.filter((d) => d.id !== id));
      onChanged?.();
    } catch (err) {
      enqueueSnackbar('Failed to delete document.', { variant: 'error' });
    }
  };

  return (
    <section className='eh-tabview'>
      <div className='export-hub-section-header'>
        <div>
          <h2 className='export-hub-section-title'>Documents</h2>
          <p className='export-hub-section-sub'>
            All files uploaded for this product.
          </p>
        </div>
        <HeaderBtn
          icon={UploadFileOutlinedIcon}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </HeaderBtn>
        <input
          ref={fileRef}
          type='file'
          style={{ display: 'none' }}
          onChange={onUploadFile}
        />
      </div>
      <div className='eh-panel'>
        {loading ? (
          <div className='eh-table-skeleton'>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className='eh-skel eh-skel-row' />
            ))}
          </div>
        ) : docs.length === 0 ? (
          <div className='eh-detail-note'>
            No documents yet — upload export paperwork or supporting files for
            this product.
          </div>
        ) : (
          <table className='runs-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Uploaded</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                <th aria-label='Actions' />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className='hoverable-row'>
                  <td>
                    <button
                      type='button'
                      className='eh-lic-doc-name'
                      onClick={() => downloadDoc(d)}
                      title='Download'
                    >
                      <DescriptionOutlinedIcon
                        style={{
                          fontSize: 14,
                          verticalAlign: 'middle',
                          marginRight: 6,
                        }}
                      />
                      {d.file_name}
                    </button>
                  </td>
                  <td>{docType(d)}</td>
                  <td>{moment(d.created_at).format('MM/DD/YYYY')}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatBytes(d.file_size)}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type='button'
                      className='eh-lic-doc-dl'
                      onClick={() => downloadDoc(d)}
                      aria-label='Download'
                    >
                      <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
                    </button>
                    <button
                      type='button'
                      className='eh-lic-doc-x'
                      onClick={() => deleteDoc(d.id)}
                      aria-label='Remove'
                    >
                      <CloseIcon style={{ fontSize: 14 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

// Comments tab — a real, persisted discussion thread for this product.
const CommentsView: React.FC<{
  productId: string;
  groupId: string | undefined;
  onChanged?: () => void;
}> = ({ productId, groupId, onChanged }) => {
  const [comments, setComments] = useState<ProductComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!groupId) return undefined;
    let active = true;
    setLoading(true);
    ExportHubAPI.getProductComments(productId, groupId)
      .then((c) => {
        if (active) setComments(c);
      })
      .catch(() => {
        if (active)
          enqueueSnackbar('Failed to load comments.', { variant: 'error' });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [productId, groupId]);

  const post = async () => {
    const body = draft.trim();
    if (!body || !groupId || posting) return;
    setPosting(true);
    try {
      const created = await ExportHubAPI.addProductComment(
        productId,
        groupId,
        body,
      );
      setComments((cs) => [...cs, created]);
      setDraft('');
      onChanged?.();
    } catch (err) {
      enqueueSnackbar('Failed to post comment.', { variant: 'error' });
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id: string) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.deleteProductComment(productId, id, groupId);
      setComments((cs) => cs.filter((c) => c.id !== id));
      onChanged?.();
    } catch (err) {
      enqueueSnackbar('Failed to delete comment.', { variant: 'error' });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      post();
    }
  };

  return (
    <section className='eh-tabview'>
      <div className='export-hub-section-header'>
        <div>
          <h2 className='export-hub-section-title'>Comments</h2>
          <p className='export-hub-section-sub'>
            Discussion thread for this product.
          </p>
        </div>
      </div>
      <div className='eh-panel'>
        {loading ? (
          <div className='eh-table-skeleton'>
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className='eh-skel eh-skel-row' />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className='eh-detail-note'>
            No comments yet — start the discussion for this product below.
          </div>
        ) : (
          <div className='eh-drawer-stack'>
            {comments.map((c) => (
              <div key={c.id} className='eh-soft-box'>
                <div className='eh-comment-head'>
                  <span className='eh-comment-author'>
                    {c.author_name || 'Unknown'}
                  </span>
                  <span className='eh-comment-time'>
                    {moment(c.created_at).fromNow()}
                    <button
                      type='button'
                      className='eh-lic-doc-x'
                      onClick={() => remove(c.id)}
                      aria-label='Delete comment'
                      style={{ marginLeft: 8 }}
                    >
                      <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                  </span>
                </div>
                <p className='eh-comment-text'>{c.body}</p>
              </div>
            ))}
          </div>
        )}
        <div className='eh-comment-compose'>
          <input
            className='export-hub-search-input'
            placeholder='Add a comment…'
            style={{ flex: 1, width: 'auto' }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={posting}
          />
          <HeaderBtn primary disabled={posting || !draft.trim()} onClick={post}>
            {posting ? 'Posting…' : 'Post'}
          </HeaderBtn>
        </div>
      </div>
    </section>
  );
};

const ProductDetail: React.FC = () => {
  const { databaseId, productId } = useParams<{
    databaseId: string;
    productId: string;
  }>();
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();
  const groupId = cur_user?.selected_team?.id;

  const [product, setProduct] = useState<ExportProduct | null>(null);
  const [dbName, setDbName] = useState<string>('Database');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [screenings, setScreenings] = useState<ScreeningResult[]>([]);
  const [classifications, setClassifications] = useState<
    ExportClassificationResult[]
  >([]);
  const [activity, setActivity] = useState<ExportActivity[]>([]);
  const [entities, setEntities] = useState<ProductEntityLink[]>([]);
  const [licenses, setLicenses] = useState<ExportLicense[]>([]);

  const [licenseWizardOpen, setLicenseWizardOpen] = useState(false);
  const [editingRegime, setEditingRegime] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [runningType, setRunningType] = useState<ExportScreeningType | null>(null);
  const [refreshing, setRefreshing] = useState<Record<string, boolean>>({});

  const [scoreOpen, setScoreOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);

  // Tab state synced to the URL (?view=), per CLAUDE.md §5 (URL-driven view).
  const view: DetailView = useMemo(() => {
    const v = new URLSearchParams(location.search).get('view');
    return VIEWS.includes(v as DetailView) ? (v as DetailView) : 'overview';
  }, [location.search]);
  const setView = (v: DetailView) => {
    const params = new URLSearchParams(location.search);
    if (v === 'overview') params.delete('view');
    else params.set('view', v);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const refreshActivity = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      setActivity(await ExportHubAPI.getProductActivity(productId, groupId));
    } catch (err) {
      console.error('Failed to load activity:', err);
    }
  }, [productId, groupId]);

  const refreshProduct = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      const p = await ExportHubAPI.getProduct(productId, groupId);
      if (p) setProduct(p);
      else setNotFound(true);
    } catch (err) {
      console.error('Failed to load product:', err);
      setNotFound(true);
    }
  }, [productId, groupId]);

  const refreshScreenings = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      setScreenings(await ExportHubAPI.getScreenings(productId, groupId));
    } catch (err) {
      console.error('Failed to load screenings:', err);
    }
  }, [productId, groupId]);

  const refreshEntities = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      setEntities(await ExportHubAPI.getLinkedEntities(productId, groupId));
    } catch (err) {
      console.error('Failed to load linked entities:', err);
    }
  }, [productId, groupId]);

  const refreshClassifications = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      setClassifications(
        await ExportHubAPI.getClassificationResults(productId, groupId),
      );
    } catch (err) {
      console.error('Failed to load classification results:', err);
    }
  }, [productId, groupId]);

  const refreshLicenses = useCallback(async () => {
    if (!productId || !groupId) return;
    try {
      setLicenses(await ExportHubAPI.getLinkedLicenses(productId, groupId));
    } catch (err) {
      console.error('Failed to load linked licenses:', err);
    }
  }, [productId, groupId]);

  // Classify this product on demand. A single PyAPI pass produces all four
  // regimes together — they are derived from one classification, not run
  // independently — so this is one action, not four.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      refreshProduct(),
      refreshScreenings(),
      refreshActivity(),
      refreshEntities(),
      refreshClassifications(),
      refreshLicenses(),
    ]).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [
    refreshProduct,
    refreshScreenings,
    refreshActivity,
    refreshEntities,
    refreshClassifications,
    refreshLicenses,
  ]);

  useEffect(() => {
    let active = true;
    if (!databaseId || !groupId) return undefined;
    ExportHubAPI.getDatabase(databaseId, groupId)
      .then((d) => {
        if (active && d?.name) setDbName(d.name);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [databaseId, groupId]);

  const runScreening = async (
    type: RunScreeningInput['check_type'],
    options?: RunScreeningInput['options'],
  ) => {
    if (!groupId || !productId) return;
    setRunningType(type);
    try {
      await ExportHubAPI.runScreening(productId, {
        check_type: type,
        group_id: groupId,
        options: options,
      });
      enqueueSnackbar('Screening completed.', { variant: 'success' });
      await Promise.all([refreshScreenings(), refreshProduct()]);
    } catch (err) {
      console.error('Screening failed:', err);
      enqueueSnackbar('Screening failed. Please try again.', { variant: 'error' });
    } finally {
      setRunningType(null);
    }
  };

  // Analyze Product (spec §1): single action → all checks + screening + coverage
  // re-evaluation. Classification produces every regime; a full refresh then
  // recomputes flags, score, and coverage from the new results.
  const analyzeProduct = async () => {
    if (!groupId || !productId) return;
    setAnalyzing(true);
    try {
      await ExportHubAPI.classifyProduct(productId, groupId);
      await Promise.all([
        refreshProduct(),
        refreshClassifications(),
        refreshScreenings(),
        refreshEntities(),
        refreshLicenses(),
        refreshActivity(),
      ]);
      enqueueSnackbar('Analyze Product complete.', { variant: 'success' });
    } catch (err) {
      console.error('Analyze failed:', err);
      enqueueSnackbar('Analyze Product failed. Please try again.', {
        variant: 'error',
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const refreshSection = async (key: string, fn: () => Promise<void>) => {
    setRefreshing((s) => ({ ...s, [key]: true }));
    try {
      await fn();
    } finally {
      setRefreshing((s) => ({ ...s, [key]: false }));
    }
  };

  // Activity-log "View in {section}" — switch to Overview and scroll to anchor.
  const jumpToSection = (section: ActivitySection) => {
    setView('overview');
    window.setTimeout(() => {
      const el = document.getElementById(section);
      if (el)
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - 84,
          behavior: 'smooth',
        });
    }, 60);
  };

  const productsHref = `/export-hub/databases/${databaseId}/products`;

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <>
        <Link to={productsHref} className='detail-back-link'>
          <ArrowBackIcon style={{ fontSize: 14 }} /> Back to Products
        </Link>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>Product not found</div>
          <div className='export-hub-empty-sub'>
            This product may have been deleted, or it belongs to another team.
          </div>
        </div>
      </>
    );
  }

  // Effective destinations (spec §4.7): intended ∪ linked entity countries.
  const effectiveDestinations = effectiveDestinationCountries(
    product.intended_destination_countries || [],
    entities,
  );
  const coveredCountries = new Set(
    licenses.flatMap(licenseCoveredCountries).map((c) => c.toLowerCase()),
  );
  const coverageGap = effectiveDestinations.filter(
    (d) => !coveredCountries.has(d.toLowerCase()),
  );

  // KPI Box 1 — client-derived compliance score (no backend score model).
  // There is no real HTS-validity service, so no HTS-invalid deduction is
  // applied (the `false` below): the code is shown as a plain value.
  const deductions = buildScoreDeductions(product, false);
  const score = computeScore(deductions);
  const { band, label: bandLabel } = scoreBand(score);

  // KPI Box 2 — system flags.
  const systemFlags = buildSystemFlags(product, false, coverageGap);

  // KPI Box 3 — risk from the latest completed export_screening (product export
  // threat) evaluation, generated on demand from the Risk Screening section.
  const complianceScreening = screenings
    .filter((s) => s.check_type === 'export_screening' && s.status === 'completed')
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))[0];
  const riskEval: ScreeningAiEvaluation | null =
    complianceScreening && 'risk' in complianceScreening.ai_evaluation
      ? (complianceScreening.ai_evaluation as ScreeningAiEvaluation)
      : null;

  // Product export risk screenings (commodity screen + opt-in AI) shown in the
  // Risk Screening section.
  const riskScreenings = screenings.filter(
    (s) => s.check_type === 'export_screening',
  );

  // "License Required" replaces the raw ECCN in Product Details — the ECCN is
  // the input, the Country Chart cross-reference is the answer. This is the
  // screening half; Product Details layers the reviewer's manual answer over
  // it, since that is the field it edits.
  const screeningRequirement = deriveLicenseRequirement(
    classifications,
    product.eccn,
  );

  return (
    <>
      <div className='export-hub-breadcrumb'>
        <Link to='/export-hub/databases' className='export-hub-crumb-link'>
          <HomeOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
          Export Hub
        </Link>
        <span className='export-hub-crumb-sep'>
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </span>
        <Link to={productsHref} className='export-hub-crumb-link'>
          {dbName}
        </Link>
        <span className='export-hub-crumb-sep'>
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </span>
        <span className='export-hub-crumb-current'>{product.name}</span>
      </div>

      <div className='export-hub-header eh-pd-header'>
        <div>
          <h1 className='export-hub-title'>{product.name}</h1>
          <div className='eh-detail-badges'>
            <span className='eh-detail-sku'>
              {product.internal_sku ? `SKU ${product.internal_sku}` : 'No SKU'}
            </span>
            <TierBadge value={product.tier} />
            <JurisdictionPill value={product.jurisdiction} />
            <StatusPill value={product.compliance_status} />
            <span className='eh-detail-meta-text'>
              {product.country_of_origin || 'Origin —'} ·{' '}
              {product.last_screened_at
                ? `Screened ${moment(product.last_screened_at).format('MM/DD/YYYY')}`
                : 'Never screened'}
            </span>
          </div>
        </div>
        <div className='export-hub-header-actions'>
          <HeaderBtn icon={SendOutlinedIcon} onClick={() => setSendOpen(true)} disabled>
            Send Package
            <span className='export-hub-preview-pill'>Coming soon</span>
          </HeaderBtn>
          <HeaderBtn icon={AddIcon} onClick={() => setLicenseWizardOpen(true)}>
            Add License
          </HeaderBtn>
          <HeaderBtn icon={PlayCircleOutlineIcon} primary onClick={analyzeProduct} disabled={analyzing}>
            {analyzing ? 'Analyzing…' : 'Analyze Product'}
          </HeaderBtn>
          <button
            className='btn-outline eh-btn-danger'
            type='button'
            onClick={() => setDeleteOpen(true)}
          >
            <DeleteOutlineIcon
              style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }}
            />
            Delete
          </button>
        </div>
      </div>

      <nav className='export-hub-tabs' aria-label='Product views'>
        <button
          type='button'
          className={`export-hub-tab${view === 'overview' ? ' active' : ''}`}
          onClick={() => setView('overview')}
        >
          <Inventory2Icon style={{ fontSize: 16 }} /> Overview
        </button>
        <button
          type='button'
          className={`export-hub-tab${view === 'transactions' ? ' active' : ''}`}
          onClick={() => setView('transactions')}
        >
          <ReceiptLongIcon style={{ fontSize: 16 }} /> Transactions
        </button>
        <button
          type='button'
          className={`export-hub-tab${view === 'documents' ? ' active' : ''}`}
          onClick={() => setView('documents')}
        >
          <DescriptionIcon style={{ fontSize: 16 }} /> Documents
        </button>
        <button
          type='button'
          className={`export-hub-tab${view === 'comments' ? ' active' : ''}`}
          onClick={() => setView('comments')}
        >
          <ChatBubbleOutlineIcon style={{ fontSize: 16 }} /> Comments
        </button>
        <button
          type='button'
          className={`export-hub-tab${view === 'history' ? ' active' : ''}`}
          onClick={() => setView('history')}
        >
          <HistoryIcon style={{ fontSize: 16 }} /> Activity Log
        </button>
      </nav>

      {view === 'transactions' ? (
        <div className='export-hub-panel'>
          <ProductTransactions
            product={product}
            groupId={groupId}
            licenses={licenses}
            onChanged={() => {
              refreshLicenses();
              refreshActivity();
            }}
          />
        </div>
      ) : view === 'documents' ? (
        <div className='export-hub-panel'>
          <DocumentsView
            productId={productId}
            groupId={groupId}
            onChanged={refreshActivity}
          />
        </div>
      ) : view === 'comments' ? (
        <div className='export-hub-panel'>
          <CommentsView
            productId={productId}
            groupId={groupId}
            onChanged={refreshActivity}
          />
        </div>
      ) : view === 'history' ? (
        <div className='export-hub-panel'>
          <ProductActivity360
            product={product}
            activity={activity}
            screenings={screenings}
            classifications={classifications}
            licenses={licenses}
            entities={entities}
            coverageGap={coverageGap}
            score={score}
            onJumpToSection={jumpToSection}
          />
        </div>
      ) : (
        <div className='export-hub-panel eh-pd-overview'>
          {/* KPI trio */}
          <div className='eh-kpi-row'>
            <ScoreCard
              score={score}
              band={band}
              bandLabel={bandLabel}
              deductionCount={deductions.filter((d) => !d.passed).length}
              onOpen={() => setScoreOpen(true)}
            />
            <SystemFlagsCard flags={systemFlags} />
            <RiskCard
              running={runningType === 'export_screening'}
              evaluation={riskEval}
              onRun={() =>
                runScreening('export_screening', { generate_risk: true })
              }
              onOpen={() => setRiskOpen(true)}
            />
          </div>

          <QuickNav />

          {/* Classification */}
          <PageSection
            id='classification'
            icon={DescriptionIcon}
            title='Classification'
            subtitle='Product identity, HTS/Schedule B, and hierarchy.'
          >
            <div className='eh-pd-two-col'>
              <EditableProductDetails
                product={product}
                groupId={groupId}
                dbName={dbName}
                onChanged={() => {
                  refreshProduct();
                  refreshActivity();
                }}
                screeningRequirement={screeningRequirement}
                onReclassify={analyzeProduct}
              />
              <ProductStructurePanel
                product={product}
                groupId={groupId}
                onChanged={() => {
                  refreshProduct();
                  refreshActivity();
                }}
              />
            </div>
          </PageSection>

          {/* Export Control Regime */}
          <PageSection
            id='jurisdiction'
            icon={VerifiedUserIcon}
            title='Export Control Regime'
            subtitle='The controlling regime and the Export Workflow findings behind it.'
            action={
              <SectionRefreshBtn
                loading={!!refreshing.jurisdiction}
                onClick={() => refreshSection('jurisdiction', refreshProduct)}
              />
            }
          >
            {/* One panel: the recorded regime on a rail, the workflow findings
                that establish it alongside. The findings are display-only —
                they come from an Export Workflow run and are imported. */}
            <div className='eh-panel'>
              <div
                className={`eh-regime-layout ${
                  editingRegime ? 'editing' : ''
                }`}
              >
                <div className='eh-regime-rail'>
                  <JurisdictionDetailsPanel
                    product={product}
                    groupId={groupId}
                    onEditingChange={setEditingRegime}
                    onChanged={() => {
                      refreshProduct();
                      refreshActivity();
                    }}
                  />
                </div>
                <div className='eh-regime-main'>
                  <ExportWorkflowResults
                    product={product}
                    classifications={classifications}
                    databaseId={databaseId}
                  />
                </div>
              </div>
            </div>
          </PageSection>

          {/* Risk Screening — product export threat eval (drives the Risk KPI) */}
          <PageSection
            id='screening'
            icon={SearchIcon}
            title='Risk Screening'
            subtitle='Commodity screen (auto on upload) plus an opt-in AI export threat evaluation that drives the Risk score.'
            action={
              <SectionRefreshBtn
                loading={!!refreshing.screening}
                onClick={() => refreshSection('screening', refreshScreenings)}
              />
            }
          >
            <RiskScreening
              screenings={riskScreenings}
              running={runningType === 'export_screening'}
              onRunScreen={() => runScreening('export_screening')}
              htsCode={product.hts_code}
            />
          </PageSection>

          {/* Licenses */}
          <PageSection
            id='licenses'
            icon={ArticleIcon}
            title='Licenses'
            subtitle='Linked export licenses and coverage against effective destinations.'
          >
            <LicenseSummary licenses={licenses} />
            <LinkedLicenses
              product={product}
              groupId={groupId}
              effectiveDestinations={effectiveDestinations}
              onChanged={() => {
                refreshProduct();
                refreshLicenses();
                refreshActivity();
              }}
            />
          </PageSection>

          {/* Entities */}
          <PageSection
            id='entities'
            icon={BusinessIcon}
            title='Entities'
            subtitle='Linked parties and effective destinations.'
          >
            <div className='eh-panel'>
              <div className='eh-entity-layout'>
                <CustomersConsignees
                  product={product}
                  groupId={groupId}
                  entities={entities}
                  onChanged={() => {
                    refreshEntities();
                    refreshActivity();
                  }}
                />
              </div>
            </div>
          </PageSection>
        </div>
      )}

      {/* Drawers */}
      <ScoreDrawer
        open={scoreOpen}
        onClose={() => setScoreOpen(false)}
        score={score}
        band={band}
        bandLabel={bandLabel}
        deductions={deductions}
      />
      <RiskDrawer
        open={riskOpen}
        onClose={() => setRiskOpen(false)}
        evaluation={riskEval}
      />

      <DeleteProductModal
        open={deleteOpen}
        product={product}
        groupId={groupId}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          history.push(productsHref);
        }}
      />

      <LicenseRequestWizard
        open={licenseWizardOpen}
        databaseId={product.database_id || databaseId}
        groupId={groupId}
        lockedProductId={product.id}
        lockedProductName={product.name}
        onClose={() => setLicenseWizardOpen(false)}
        onCreated={() => {
          setLicenseWizardOpen(false);
          refreshProduct();
          refreshLicenses();
          refreshActivity();
        }}
      />
      <SendPackageDialog
        open={sendOpen}
        subjectLabel={product.name}
        onClose={() => setSendOpen(false)}
      />
    </>
  );
};

export default ProductDetail;
